import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import FavoriteService, { Favorite } from '../services/FavoriteService';

const Wishlist: React.FC = () => {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(false);
  const [openSizeIndex, setOpenSizeIndex] = useState<number | null>(null);
  const [gridMode, setGridMode] = useState(false); // Grid modu için state
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    const fetchFavorites = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          const favs = await FavoriteService.getFavorites(token);
          setFavorites(favs);
        }
      } catch (e) {
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, [user, navigate]);

  if (!user) return null;

  const handleRemoveFavorite = async (fav: Favorite) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      await FavoriteService.removeFavorite({ 
        token, 
        costumeId: fav.costumeId, 
        accessoryId: fav.accessoryId 
      });
      setFavorites(favorites.filter(f => f.id !== fav.id));
    } catch (e) {
      console.error('Favori silme hatası:', e);
    }
  };

  // Grid modunu toggle et
  const toggleGridMode = () => {
    setGridMode(!gridMode);
  };

  return (
    <div className="min-h-screen bg-white">
      <div style={{
        padding: '2rem 0 0 2rem',
        paddingRight: '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <h1 style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: '1.6rem',
            letterSpacing: '0.01em',
            textAlign: 'left',
            margin: 0,
            textTransform: 'none'
          }}>Favorilerim</h1>
          
          {/* Grid Toggle Butonu - Sadece Mobile */}
          <div className="view-toggle-buttons">
            <button
              onClick={toggleGridMode}
              style={{
                padding: '0.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                background: gridMode ? '#111827' : '#ffffff',
                color: gridMode ? '#ffffff' : '#111827',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              title="4'lü Grid Görünümü"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h7v7H3V3zm0 11h7v7H3v-7zm11-11h7v7h-7V3zm0 11h7v7h-7v-7z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div>Yükleniyor...</div>
      ) : favorites.length === 0 ? (
        <div className="text-gray-500">Henüz favori ürününüz yok.</div>
      ) : (
        <div 
          className={`newcostumes-grid ${gridMode ? 'mobile-grid-mode' : ''}`}
        >
          {favorites.map((fav, idx) => (
            <div key={fav.id} className="newcostumes-card" style={{
              position: 'relative'
            }}>
              <Link 
                to={fav.costume ? `/costume/${fav.costume.id}` : `/accessories/${fav.accessory?.id}`}
                style={{
                  textDecoration: 'none', 
                  color: 'inherit'
                }}
              >
                <div className="newcostumes-image-container">
                  <img
                    src={fav.costume?.image || fav.accessory?.image}
                    alt={fav.costume?.name || fav.accessory?.name}
                  />
                </div>
                <div className="newcostumes-info">
                  <div className="text-black text-base font-normal leading-tight" translate="no">
                    {fav.costume?.name || fav.accessory?.name}
                  </div>
                  <div className="text-black text-lg font-bold">
                    {fav.costume?.price || fav.accessory?.price}
                  </div>
                  {/* Kostüm bedenleri */}
                  {fav.costume?.size && (
                    <div className="beden-row">
                      <span className="text-xs text-gray-400 mr-2 cursor-pointer" onClick={(e) => {
                        e.preventDefault();
                        setOpenSizeIndex(openSizeIndex === idx ? null : idx);
                      }}>Beden seç</span>
                      {openSizeIndex === idx && fav.costume.size && fav.costume.size.length > 0 && (
                        fav.costume.size.split(',').map((size: string, sidx: number) => (
                          <span key={sidx} className="size-box">{size}</span>
                        ))
                      )}
                    </div>
                  )}
                  {/* Aksesuar bedenleri */}
                  {fav.accessory?.size && (
                    <div className="beden-row">
                      <span className="text-xs text-gray-400 mr-2 cursor-pointer" onClick={(e) => {
                        e.preventDefault();
                        setOpenSizeIndex(openSizeIndex === idx ? null : idx);
                      }}>Beden seç</span>
                      {openSizeIndex === idx && fav.accessory.size && fav.accessory.size.length > 0 && (
                        fav.accessory.size.map((size: string, sidx: number) => (
                          <span key={sidx} className="size-box">{size}</span>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </Link>
              <span
                style={{position: 'absolute', right: 12, bottom: 12, zIndex: 10}}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFavorite(fav);
                }}
              >
                <svg
                  width="20"
                  height="20"
                  fill="#ef4444"
                  stroke="#ef4444"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  className="hover:text-red-500 cursor-pointer"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Wishlist; 